import { useEffect, useRef, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { add, isAfter, isWithinInterval, parseISO } from 'date-fns';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { SpecialtySelectionModal } from '@/modules/referrals/components/SpecialtySelectionModal';
import { HuddleReferralModal } from '@/modules/referrals/HuddleReferralModal';
import { useAppContext } from '@/utils/hooks/useAppContext';

import { useGetDateFNSLocale } from '../../../locales/helpers';
import type { Appointment } from '../../../react-query/useAppointments';
import type { TFmxSummary } from '../../../react-query/useFmxSummaries';
import type { GetTaskResponse } from '../../../react-query/useGetTasks';
import { usePreferences } from '../../../react-query/usePreferences';
import type { TTreatmentCounts } from '../../../react-query/useTreatmentCounts';
import type { TVisitSummary } from '../../../react-query/useVisitSummaries';
import { useSearchParams } from '../../../utils/hooks/useSearchParams';
import { parseAndFormatDate } from '../../../utils/parseDate';
import { useShouldHideHuddle } from '../useShouldHideHuddle';

import { AppointmentHeaderContent } from './AppointmentHeaderContent';
import { Findings } from './findings/findings';
import { AppointmentPatientNameGuard } from './patientStripRedesigned/AppointmentPatientNameGuard';
import { getPatientAge } from './patientStripRedesigned/patientInfoSheet/PatientInfo';
import { PatientStripRedesigned } from './patientStripRedesigned/patientStripRedesigned';
import { ReferralStatusPill } from './patientStripRedesigned/ReferralStatusPill';
import { useReferralActions } from './useReferralActions';
import { useReviewRadiographs } from './useReviewRadiographs';

export type IndicatorValue = {
  toothNumber: string;
  dmf: number;
  imageManifestId?: number[] | number;
  potentialAction?: boolean;
  treatmentStatus?: string | undefined;
}[];

export type SrpIndicatorValue = {
  quad: string;
  potentialAction?: boolean;
  treatmentStatus?: string;
  imageManifestIds?: number[] | number;
}[];

export type Indicator = {
  type: 'potentialCrown' | 'potentialIncipient' | 'potentialSrp';
  values?: IndicatorValue;
  srpValues?: SrpIndicatorValue;
  clearBackground?: boolean;
};

export interface PatientStripProps {
  appointment: Appointment;
  fmxSummary?: TFmxSummary;
  index: number;
  patientTask?: GetTaskResponse;
  treatmentCounts?: TTreatmentCounts | undefined;
  visitSummary?: TVisitSummary | undefined;
  lastViewedAppointmentRef?: React.RefObject<HTMLDivElement>;
  onOpenSheet?: (
    appointment: Appointment,
    source: 'PATIENT_INFO' | 'ICON',
  ) => void;
  isLoadingVisitSummaries?: boolean;
}

export const PatientStrip = ({
  appointment,
  fmxSummary,
  index,
  patientTask,
  treatmentCounts,
  visitSummary,
  lastViewedAppointmentRef,
  onOpenSheet,
  isLoadingVisitSummaries,
}: PatientStripProps) => {
  const [isGlowing, setIsGlowing] = useState(false);
  const patientStripContainerRef = useRef<HTMLDivElement | null>(null);
  const privacyOn = useSelector((state: any) => state.login?.user?.privacyOn);
  const hideHuddle = useShouldHideHuddle();
  const searchParams = useSearchParams();
  const dateFNSLocale = useGetDateFNSLocale();
  const { data: prefs } = usePreferences();
  const history = useHistory();
  const {
    isOpenReferModal,
    setIsOpenReferModal,
    isOpenSpecialtyModal,
    setIsOpenSpecialtyModal,
    handleOpenReferModal,
    handleFlagReferral,
    handleSpecialtyConfirm,
    handleUnmarkReferral,
    handleResubmitReferral,
    isResubmitting,
    isResubmitSuccess,
    isResubmitError,
    resubmitData,
    resetResubmit,
  } = useReferralActions(appointment, prefs?.enableVideaReferral);

  const patientDOB = appointment.patient?.dob;
  const formattedDOB = parseAndFormatDate(patientDOB, undefined, dateFNSLocale);

  const age = getPatientAge(patientDOB);

  const appointmentStart = parseISO(appointment.appointment.start);

  const appointmentEnd =
    appointmentStart &&
    (appointment.appointment.end &&
    isAfter(parseISO(appointment.appointment.end), appointmentStart)
      ? parseISO(appointment.appointment.end)
      : add(appointmentStart, {
          hours: 1,
        }));

  const isCurrentAppointment =
    (appointmentStart &&
      appointmentEnd &&
      isWithinInterval(new Date(), {
        start: appointmentStart,
        end: appointmentEnd,
      })) ||
    false;

  useEffect(() => {
    let clearGlowTimeout: NodeJS.Timeout | undefined;

    const uaid = searchParams.get('uaid');

    if (uaid) {
      if (appointment.appointment.unifiedId === uaid) {
        patientStripContainerRef.current?.scrollIntoView();
        setIsGlowing(true);
        clearGlowTimeout = setTimeout(() => {
          setIsGlowing(false);
        }, 2000);
      }
    }

    return () => {
      if (clearGlowTimeout) {
        clearTimeout(clearGlowTimeout);
      }
    };
  }, [searchParams, appointment.appointment.unifiedId]);

  const { onReviewRadiographs, annotatorUri } =
    useReviewRadiographs(appointment);

  const hasScheduledTreatments =
    !!treatmentCounts?.scheduled && treatmentCounts.scheduled > 0;
  const hasOutstandingTreatments =
    !!treatmentCounts?.outstanding && treatmentCounts.outstanding > 0;
  const treatmentsShown: string[] = [];
  if (hasScheduledTreatments) {
    treatmentsShown.push('scheduled');
  }
  if (hasOutstandingTreatments) {
    treatmentsShown.push('outstanding');
  }

  if (!hideHuddle) {
    return (
      <>
        <PatientStripRedesigned
          appointment={appointment}
          visitSummary={visitSummary}
          patientTask={patientTask}
          index={index}
          onReviewRadiographs={onReviewRadiographs}
          annotatorUri={annotatorUri}
          lastViewedAppointmentRef={lastViewedAppointmentRef}
          onOpenSheet={onOpenSheet}
          onOpenReferModal={handleOpenReferModal}
          onFlagReferral={handleFlagReferral}
          onUnmarkReferral={handleUnmarkReferral}
          onResubmitReferral={handleResubmitReferral}
          isResubmitting={isResubmitting}
          isResubmitSuccess={isResubmitSuccess}
          isResubmitError={isResubmitError}
          resubmitData={resubmitData}
          resetResubmit={resetResubmit}
          isLoadingVisitSummaries={isLoadingVisitSummaries}
        />
        <HuddleReferralModal
          isOpen={isOpenReferModal}
          onClose={() => setIsOpenReferModal(false)}
          appointment={appointment}
        />
        <SpecialtySelectionModal
          isOpen={isOpenSpecialtyModal}
          onClose={() => setIsOpenSpecialtyModal(false)}
          onConfirm={handleSpecialtyConfirm}
          isLoading={isResubmitting}
        />
      </>
    );
  }

  if (hideHuddle) {
    return (
      <>
        <div
          data-cy="patient-row"
          className={`vai-relative vai-flex vai-bg-gray-50 vai-rounded-xl vai-text-base vai-mb-2.5 vai-overflow-hidden vai-transition-shadow vai-duration-300 vai-ease-in-out ${
            isGlowing ? 'vai-shadow-[0_0_8px_2px_rgba(11,121,208,1)]' : ''
          } ${
            isCurrentAppointment
              ? 'vai-bg-gradient-to-br vai-from-blue-50/15 vai-via-blue-100/25 vai-to-blue-50/15'
              : ''
          } ${hideHuddle ? 'huddle-redesign' : ''}`}
          ref={patientStripContainerRef}
        >
          <div
            onClick={() => {
              onReviewRadiographs();
              history.push(annotatorUri);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (appointment.patient.imagingPatientId) {
                  onReviewRadiographs();
                  history.push(annotatorUri);
                }
              }
            }}
            role="button"
            tabIndex={appointment.patient.imagingPatientId ? 0 : -1}
            data-testid={`patient-strip-${appointment.appointment.unifiedId}`}
            className="vai-text-left vai-flex-1 vai-flex vai-gap-4 vai-p-4 vai-pl-10 vai-items-start vai-cursor-pointer"
            style={{
              pointerEvents: !appointment.patient.imagingPatientId
                ? 'none'
                : 'auto',
            }}
          >
            <div className="vai-flex-grow vai-flex vai-flex-col vai-items-start vai-gap-2 vai-p-0 vai-h-fit">
              <div className="vai-flex vai-flex-col vai-text-black">
                <div className="vai-flex vai-flex-row vai-gap-2 vai-mb-2 vai-items-center">
                  {prefs?.enableVideaReferral && (
                    <ReferralStatusPill
                      unifiedAppointmentId={appointment.appointment.unifiedId}
                    />
                  )}
                </div>
                <div className="text-base font-medium leading-tight">
                  <AppointmentPatientNameGuard
                    appointment={appointment}
                    index={index}
                  />
                </div>
                <div className="vai-flex vai-items-center">
                  <span
                    className="vai-text-sm vai-font-thin"
                    data-testid="patient-age-and-dob"
                  >
                    {patientDOB && (
                      <span>
                        {privacyOn ? (
                          <Trans>
                            Age {age} | DOB: {formattedDOB}
                          </Trans>
                        ) : (
                          <span className="vai-text-gray-300 vai-blur-[5px] vai-select-none vai-font-mono vai-text-sm vai-font-thin">
                            &#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <AppointmentHeaderContent appointment={appointment} />
            </div>
          </div>

          <Findings
            appointment={appointment}
            fmxSummary={fmxSummary}
            onReviewRadiographs={onReviewRadiographs}
            patientTask={patientTask}
            onOpenReferModal={handleOpenReferModal}
            onFlagReferral={handleFlagReferral}
            onUnmarkReferral={handleUnmarkReferral}
            onResubmitReferral={handleResubmitReferral}
          />
        </div>
        <HuddleReferralModal
          isOpen={isOpenReferModal}
          onClose={() => setIsOpenReferModal(false)}
          appointment={appointment}
        />
        <SpecialtySelectionModal
          isOpen={isOpenSpecialtyModal}
          onClose={() => setIsOpenSpecialtyModal(false)}
          onConfirm={handleSpecialtyConfirm}
          isLoading={isResubmitting}
        />
      </>
    );
  }
};
